import React, { useState, useCallback, useRef } from 'react';
import { Button } from './ui';

interface FileUploadProps {
  onFileSelect: (file: File) => void;
  onUpload?: (file: File) => Promise<void>;
  isLoading?: boolean;
  maxFileSize?: number; // en MB
  acceptedFormats?: string[];
  multiple?: boolean;
  disabled?: boolean;
  className?: string;
}

interface UploadedFile {
  file: File;
  preview?: string;
  error?: string;
}

export function FileUpload({
  onFileSelect,
  onUpload,
  isLoading = false,
  maxFileSize = 10,
  acceptedFormats = ['image/jpeg', 'image/png', 'image/webp', 'application/pdf'],
  multiple = false,
  disabled = false,
  className = '',
}: FileUploadProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);
  const [dragActive, setDragActive] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Valider le fichier
  const validateFile = useCallback((file: File): string | null => {
    // Vérifier le type
    if (!acceptedFormats.includes(file.type)) {
      return `Format non supporté. Formats acceptés: ${acceptedFormats.map(f => f.split('/')[1]).join(', ')}`;
    }

    // Vérifier la taille
    if (file.size > maxFileSize * 1024 * 1024) {
      return `Fichier trop volumineux. Taille maximale: ${maxFileSize}MB`;
    }

    return null;
  }, [acceptedFormats, maxFileSize]);

  // Créer la prévisualisation pour les images
  const createPreview = useCallback((file: File): Promise<string | undefined> => {
    return new Promise((resolve) => {
      if (file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onload = (e) => resolve(e.target?.result as string);
        reader.onerror = () => resolve(undefined);
        reader.readAsDataURL(file);
      } else {
        resolve(undefined);
      }
    });
  }, []);

  // Gérer la sélection de fichiers
  const handleFileSelect = useCallback(async (files: FileList | null) => {
    if (!files || disabled) return;

    setError(null);
    const fileArray = Array.from(files);

    // Valider et traiter chaque fichier
    const validFiles: UploadedFile[] = [];

    for (const file of fileArray) {
      const validationError = validateFile(file);

      if (validationError) {
        setError(validationError);
        continue;
      }

      const preview = await createPreview(file);
      validFiles.push({
        file,
        preview,
      });

      // Appeler le callback de sélection
      onFileSelect(file);
    }

    if (validFiles.length > 0) {
      setUploadedFiles(prev => multiple ? [...prev, ...validFiles] : validFiles);
    }
  }, [disabled, validateFile, createPreview, onFileSelect, multiple]);

  // Gérer le drag and drop
  const handleDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (disabled) return;

    handleFileSelect(e.dataTransfer.files);
  }, [disabled, handleFileSelect]);

  // Ouvrir le sélecteur de fichiers
  const openFileSelector = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  // Supprimer un fichier
  const removeFile = useCallback((index: number) => {
    setUploadedFiles(prev => prev.filter((_, i) => i !== index));
  }, []);

  // Télécharger un fichier
  const uploadFile = useCallback(async (fileData: UploadedFile, index: number) => {
    if (!onUpload) return;

    try {
      await onUpload(fileData.file);
      // Marquer comme téléchargé (optionnel)
      setUploadedFiles(prev =>
        prev.map((item, i) =>
          i === index ? { ...item, error: undefined } : item
        )
      );
    } catch (err: any) {
      setUploadedFiles(prev =>
        prev.map((item, i) =>
          i === index ? { ...item, error: err.message || 'Erreur lors du téléchargement' } : item
        )
      );
    }
  }, [onUpload]);

  return (
    <div className={`space-y-4 ${className}`}>
      {/* Zone de drop */}
      <div
        className={`relative border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
          dragActive
            ? 'border-blue-400 bg-blue-50'
            : 'border-gray-300 hover:border-gray-400'
        } ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
        onClick={!disabled ? openFileSelector : undefined}
      >
        <input
          ref={fileInputRef}
          type="file"
          multiple={multiple}
          accept={acceptedFormats.join(',')}
          onChange={(e) => handleFileSelect(e.target.files)}
          className="hidden"
          disabled={disabled}
        />

        <div className="space-y-2">
          <svg
            className="mx-auto h-12 w-12 text-gray-400"
            stroke="currentColor"
            fill="none"
            viewBox="0 0 48 48"
          >
            <path
              d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
              strokeWidth={2}
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>

          <div className="text-sm text-gray-600">
            <span className="font-medium text-blue-600 hover:text-blue-500">
              Cliquez pour sélectionner
            </span>{' '}
            ou glissez-déposez vos fichiers ici
          </div>

          <p className="text-xs text-gray-500">
            Formats: {acceptedFormats.map(f => f.split('/')[1].toUpperCase()).join(', ')} •
            Max: {maxFileSize}MB
            {multiple && ' • Fichiers multiples autorisés'}
          </p>
        </div>
      </div>

      {/* Erreur globale */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <div className="text-sm text-red-600">{error}</div>
        </div>
      )}

      {/* Liste des fichiers sélectionnés */}
      {uploadedFiles.length > 0 && (
        <div className="space-y-3">
          <h4 className="text-sm font-medium text-gray-900">
            Fichiers sélectionnés ({uploadedFiles.length})
          </h4>

          <div className="space-y-2">
            {uploadedFiles.map((fileData, index) => (
              <div
                key={index}
                className="flex items-center space-x-3 p-3 border border-gray-200 rounded-lg"
              >
                {/* Prévisualisation */}
                {fileData.preview ? (
                  <img
                    src={fileData.preview}
                    alt={fileData.file.name}
                    className="w-12 h-12 object-cover rounded"
                  />
                ) : (
                  <div className="w-12 h-12 bg-gray-100 rounded flex items-center justify-center">
                    <svg className="w-6 h-6 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  </div>
                )}

                {/* Informations du fichier */}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">
                    {fileData.file.name}
                  </p>
                  <p className="text-sm text-gray-500">
                    {(fileData.file.size / 1024 / 1024).toFixed(2)} MB • {fileData.file.type}
                  </p>
                  {fileData.error && (
                    <p className="text-sm text-red-600">{fileData.error}</p>
                  )}
                </div>

                {/* Actions */}
                <div className="flex space-x-2">
                  {onUpload && (
                    <Button
                      size="sm"
                      onClick={() => uploadFile(fileData, index)}
                      isLoading={isLoading}
                      disabled={isLoading}
                    >
                      Télécharger
                    </Button>
                  )}

                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => removeFile(index)}
                    disabled={isLoading}
                  >
                    ✕
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Bouton d'action principal */}
      {onUpload && uploadedFiles.length > 0 && (
        <div className="flex justify-center">
          <Button
            onClick={() => Promise.all(uploadedFiles.map((fileData, index) => uploadFile(fileData, index)))}
            isLoading={isLoading}
            disabled={isLoading || uploadedFiles.some(f => f.error)}
          >
            {isLoading ? 'Téléchargement...' : `Télécharger ${uploadedFiles.length} fichier${uploadedFiles.length > 1 ? 's' : ''}`}
          </Button>
        </div>
      )}
    </div>
  );
}